import { useRouter } from 'next/router';
import { PagePosInNavStructure } from '@models/Document';

const isAbsoluteHref = (href: string): boolean => href.startsWith('http') || href.startsWith('/');
const isMailHref = (href: string): boolean => href.startsWith('mailto:');
const isAnchorHref = (href: string): boolean => href.startsWith('#');

/**
 * Resolves a relative URL against a base URL using the browser URL API.
 * This prevents path segment accumulation that occurs with path.join().
 */
const resolveRelativeUrl = (baseUrl: string, relativeHref: string): string => {
  try {
    // Use URL API to properly resolve relative paths
    const resolved = new URL(relativeHref, baseUrl);
    return resolved.pathname + resolved.search + resolved.hash;
  } catch (error) {
    // Fallback: if URL parsing fails, return the href as-is
    console.error('[useMdLink] Error resolving URL:', error);
    return relativeHref;
  }
};

export const useMdLink = (href: string, navStructure?: PagePosInNavStructure): string => {
  const router = useRouter();

  // Return as-is for absolute URLs, mailto links, and anchor links
  if (isAbsoluteHref(href) || isMailHref(href) || isAnchorHref(href)) {
    return href;
  }

  // Extract current path without query parameters to avoid accumulation
  const [currentPath] = router.asPath.split('?');

  // Construct a base URL for proper relative resolution
  // Use the current origin (client-side) or a placeholder (server-side)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';

  // For ROOT pages (index pages), ensure trailing slash so URL API resolves correctly
  // Example: /docs/en-US/dev-portal + ./product-management would go UP one level
  // But: /docs/en-US/dev-portal/ + ./product-management stays in the same level
  const isRootPage = navStructure === PagePosInNavStructure.ROOT;
  const normalizedPath = isRootPage && !currentPath.endsWith('/') ? `${currentPath}/` : currentPath;

  const baseUrl = `${origin}${normalizedPath}`;

  // Resolve the relative href against the current path
  return resolveRelativeUrl(baseUrl, href);
};
